/**
 * Managed Postgres and ClickHouse, in one place.
 *
 * Same arrangement as `plans.ts`: the Worker enforces what is here, the
 * dashboard prints it and the CLI reports it, so no limit is ever spelled out
 * twice. The Rust daemon in `packages/warden` mirrors the *shape* of these
 * types over the control API but deliberately does not own the policy — it
 * provisions what it is told to provision.
 *
 * Two tiers, and the difference is real rather than a price fence. A `shared`
 * instance is a database and a role on a cluster that other accounts also sit
 * on, isolated by grants and by an engine-level quota. A `dedicated` instance
 * is a container of its own with a CPU and memory bound, which costs an idle
 * footprint and so is not offered on the free plan.
 */

import { formatBytes } from "./format.js";
import type { Plan } from "./plans.js";

const MB = 1024 * 1024;
const GB = 1024 * MB;

export type DbEngine = "postgres" | "clickhouse";
export type DbTier = "shared" | "dedicated";

/**
 * `provisioning` and `deleting` are both states the daemon is working through;
 * everything else is terminal until someone acts. A `failed` instance keeps its
 * row so the reason survives long enough to be read.
 */
export type DbState = "provisioning" | "ready" | "failed" | "deleting";

export interface DbEngineSpec {
  id: DbEngine;
  label: string;
  /** What a client actually speaks to, once TLS is terminated. */
  wirePort: number;
  /** The subdomain half of the hostname handed to the user. */
  hostPrefix: string;
  tagline: string;
}

export const DB_ENGINES: Record<DbEngine, DbEngineSpec> = {
  postgres: {
    id: "postgres",
    label: "Postgres",
    wirePort: 5432,
    hostPrefix: "pg",
    tagline: "Postgres 18, the ordinary one. psql, Drizzle, Prisma, anything.",
  },
  clickhouse: {
    id: "clickhouse",
    label: "ClickHouse",
    wirePort: 9440,
    hostPrefix: "ch",
    tagline: "Columnar, for the queries Postgres takes minutes over.",
  },
};

export const DB_ENGINE_IDS: DbEngine[] = ["postgres", "clickhouse"];

export const isDbEngine = (value: string): value is DbEngine =>
  value === "postgres" || value === "clickhouse";

export const isDbTier = (value: string): value is DbTier =>
  value === "shared" || value === "dedicated";

/** What one plan may hold, per engine. Counted over live instances only. */
export interface DbAllowance {
  /** How many `shared` instances of this engine the account may hold at once. */
  shared: number;
  /** How many `dedicated` ones. Zero means the tier is not offered. */
  dedicated: number;
  /** On-disk ceiling for one instance. The daemon stops writes at this. */
  storageBytes: number;
  /** Server-side connection limit, enforced by the proxy and by the engine. */
  maxConnections: number;
  /** Memory bound for a dedicated container. Ignored on the shared tier. */
  dedicatedMemoryBytes: number;
  /** CPU bound for a dedicated container, in whole cores. */
  dedicatedCpus: number;
}

/**
 * Free gets one of each, small, shared. The point is that someone can point a
 * side project at it the same afternoon they sign up — not that it is a
 * meaningful amount of storage.
 */
export const DB_ALLOWANCES: Record<Plan, Record<DbEngine, DbAllowance>> = {
  free: {
    postgres: {
      shared: 1,
      dedicated: 0,
      storageBytes: 512 * MB,
      maxConnections: 10,
      dedicatedMemoryBytes: 0,
      dedicatedCpus: 0,
    },
    clickhouse: {
      shared: 1,
      dedicated: 0,
      storageBytes: 2 * GB,
      maxConnections: 10,
      dedicatedMemoryBytes: 0,
      dedicatedCpus: 0,
    },
  },
  pro: {
    postgres: {
      shared: 5,
      dedicated: 2,
      storageBytes: 20 * GB,
      maxConnections: 100,
      dedicatedMemoryBytes: 4 * GB,
      dedicatedCpus: 2,
    },
    clickhouse: {
      shared: 5,
      dedicated: 2,
      storageBytes: 100 * GB,
      maxConnections: 100,
      dedicatedMemoryBytes: 8 * GB,
      dedicatedCpus: 4,
    },
  },
};

export const allowanceFor = (plan: Plan, engine: DbEngine): DbAllowance =>
  (DB_ALLOWANCES[plan] ?? DB_ALLOWANCES.free)[engine];

/** How many of this tier the plan permits at once. */
export const seatsFor = (plan: Plan, engine: DbEngine, tier: DbTier): number => {
  const allowance = allowanceFor(plan, engine);
  return tier === "shared" ? allowance.shared : allowance.dedicated;
};

/**
 * One managed instance, as the dashboard and the CLI see it. The password is
 * absent on purpose — it exists exactly once, in the response to the call that
 * created or rotated it, and is never stored in a readable form afterwards.
 */
export interface ManagedDatabase {
  id: string;
  engine: DbEngine;
  tier: DbTier;
  /** The account's own name for it, unique per owner per engine. */
  name: string;
  state: DbState;
  /** Why it is `failed`. Null in every other state. */
  error: string | null;

  // ---- how to connect -----------------------------------------------------
  host: string;
  port: number;
  /** The wire username. Derived from the owner and the name, never chosen. */
  login: string;
  /** The database (Postgres) or schema (ClickHouse) the role owns. */
  database: string;

  // ---- what it is doing ---------------------------------------------------
  /** Bytes on disk, as of `usageAt`. Zero until the daemon has measured once. */
  sizeBytes: number;
  /** When the daemon last measured, ISO-8601. Null before the first sweep. */
  usageAt: string | null;
  maxConnections: number;
  storageBytes: number;
  createdAt: string;
}

/** Handed over exactly once, at creation or rotation. */
export interface DbCredentials {
  database: ManagedDatabase;
  password: string;
  /** Ready to paste: `postgresql://…` or `clickhouse://…`, TLS required. */
  url: string;
}

export interface DbCreateRequest {
  engine: DbEngine;
  tier: DbTier;
  name: string;
}

export interface DatabaseListResponse {
  databases: ManagedDatabase[];
  /** What the account may still create, folded per engine and tier. */
  remaining: Record<DbEngine, Record<DbTier, number>>;
  /**
   * Whether this deployment can actually provision one.
   *
   * A deployment with no daemon configured serves the marketing page, the docs
   * and this list perfectly well, and then refuses every create with a 503.
   * Letting the dashboard find that out by trying is the wrong way round: the
   * account did nothing wrong and there is nothing they can do about it, so the
   * form should not be there in the first place.
   *
   * Never a reason to hide a database that already exists — an instance
   * provisioned before the daemon was unconfigured is still listed, still
   * connectable, and still deletable.
   */
  available: boolean;
}

/**
 * Instance names go into a Postgres identifier and a hostname, so they are
 * narrower than a file slug: lowercase, no leading digit, no trailing dash.
 */
const DB_NAME_RE = /^[a-z](?:[a-z0-9-]{0,30}[a-z0-9])?$/;

/** Names the engines themselves own, which a tenant must not be able to claim. */
const RESERVED_DB_NAMES = new Set([
  "admin",
  "clickhouse",
  "default",
  "information_schema",
  "postgres",
  "public",
  "saucer",
  "system",
  "template0",
  "template1",
  "warden",
]);

export const isValidDbName = (value: string): boolean =>
  DB_NAME_RE.test(value) && !RESERVED_DB_NAMES.has(value);

/**
 * A short, stable discriminator for an owner, so a truncated username cannot
 * collide with a different one.
 *
 * FNV-1a rather than a cryptographic hash: this is a uniqueness tiebreaker, not
 * a secret, and it has to run identically in the Worker and in the browser
 * without pulling in `crypto.subtle`'s async API.
 */
function ownerTag(owner: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < owner.length; index += 1) {
    hash ^= owner.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36).padStart(7, "0").slice(0, 7);
}

/**
 * The wire identity, derived rather than chosen.
 *
 * Postgres truncates identifiers at 63 bytes, and a username may be 38
 * characters with a name of 32 — so the owner half has to be shortened. It is
 * *not* enough to slice it: two usernames sharing a 24-character prefix would
 * produce the same login, and `databases_login_idx` is unique globally rather
 * than per owner, so the second account's create would fail with "you already
 * have a database by that name" about a stranger's row.
 *
 * So the owner half is a 16-character slice plus a 7-character hash of the
 * *whole* username. Distinct usernames therefore give distinct logins, and the
 * worst case is 16 + 1 + 7 + 1 + 32 = 57 characters, inside the limit.
 */
export const loginFor = (owner: string, name: string): string =>
  `${owner.slice(0, 16)}${ownerTag(owner)}_${name}`.replace(/-/g, "_");

/** The database a tenant owns. Same derivation, so the two always agree. */
export const databaseNameFor = (owner: string, name: string): string => loginFor(owner, name);

/** The hostname for an engine, e.g. `pg.saucer.sh`. */
export const hostFor = (engine: DbEngine, root: string): string =>
  `${DB_ENGINES[engine].hostPrefix}.${root}`;

/**
 * A connection string with the password in it. Built here rather than in the
 * daemon so the dashboard, the CLI and the assistant all print the same thing.
 * `sslmode=require` is not a suggestion: the proxy refuses plaintext.
 */
export function connectionUrl(database: ManagedDatabase, password: string): string {
  const auth = `${encodeURIComponent(database.login)}:${encodeURIComponent(password)}`;
  const where = `${database.host}:${database.port}/${database.database}`;
  return database.engine === "postgres"
    ? `postgresql://${auth}@${where}?sslmode=require`
    : `clickhouse://${auth}@${where}?secure=true`;
}

/** The same instance as a one-line shell command, for the docs and the CLI. */
export function connectionCommand(database: ManagedDatabase, password: string): string {
  return database.engine === "postgres"
    ? `psql "${connectionUrl(database, password)}"`
    : `clickhouse-client --host ${database.host} --port ${database.port} --secure` +
        ` --user ${database.login} --password '${password}' --database ${database.database}`;
}

/** Storage as the dashboard shows it, matching how file quotas are printed. */
export const formatDbBytes = (bytes: number): string => formatBytes(bytes).replace(/\.0 /, " ");
